import { defaultOrganizationBankAccountInput, normalizeOrganizationBankAccountIban, organizationBankAccountSchema, } from "./organization-bank-account";
import { isPossiblePhoneNumberValue } from "@/shared/utils/phone-number";
export function createDefaultOrganizationWizardState() {
    return {
        org_type: "",
        organization: {
            name: "",
            legal_id: "",
            email: "",
            phone: "",
            country: "CR",
            country_name: "",
            location_one: "",
            location_two: "",
            location_three: "",
            image_storage_id: null,
            description: "",
            secondary_contact: "",
        },
        bank_accounts: [{ ...defaultOrganizationBankAccountInput }],
        billing_setup: {
            plan_id: "",
            plan_price_id: "",
            payment_method_type: "onvo_card",
            trial: {
                enabled: true,
                days: 5,
            },
            grace_days_after_due: 3,
            discounts: [],
        },
        owner: {
            assigned_person: "",
            email: "",
            password: "",
        },
    };
}
export function buildCreateOrganizationPayload(state) {
    const organization = state.organization;
    const owner = {
        dni: organization.legal_id.trim(),
        company_name: organization.name.trim(),
        assigned_person: state.owner.assigned_person.trim(),
        email: state.owner.email.trim(),
        contact_phone: organization.phone.trim() || null,
        country: organization.country,
        location_one: organization.location_one,
        location_two: organization.location_two,
        password: state.owner.password,
        ip_address: "127.0.0.1",
        image_storage_id: organization.image_storage_id,
    };
    const payload = {
        display_name: organization.name.trim(),
        org_type: state.org_type,
        owner,
        bank_accounts: state.bank_accounts.map((account) => ({
            bank_name: account.bank_name.trim(),
            beneficiary_name: account.beneficiary_name.trim(),
            iban: normalizeOrganizationBankAccountIban(account.iban),
            currency: account.currency,
        })),
    };
    if (state.org_type === "concierge") {
        payload.billing_setup = {
            plan_id: state.billing_setup.plan_id,
            plan_price_id: state.billing_setup.plan_price_id,
            payment_method_type: state.billing_setup.payment_method_type,
            trial: {
                enabled: state.billing_setup.trial.enabled,
                days: state.billing_setup.trial.enabled
                    ? state.billing_setup.trial.days
                    : null,
            },
            grace_days_after_due: state.billing_setup.grace_days_after_due ?? 0,
            discounts: state.billing_setup.discounts,
        };
    }
    if (state.org_type === "provider") {
        payload.provider_profile = {
            dni: organization.legal_id.trim(),
            name: organization.name.trim(),
            primary_contact: organization.phone.trim(),
            second_contact: organization.secondary_contact.trim() || null,
            email: organization.email.trim(),
            location_one: organization.location_one,
            location_two: organization.location_two,
            location_three: organization.location_three.trim() || null,
            country: organization.country,
            country_name: organization.country_name || null,
            description: organization.description.trim(),
        };
    }
    return payload;
}
export function validateOrganizationWizardStep(state, step) {
    const errors = {};
    if (step === "type") {
        if (!state.org_type) {
            errors.org_type = "Seleccione el tipo de organización.";
        }
        return errors;
    }
    if (step === "organization") {
        const organization = state.organization;
        requireText(errors, "organization.name", organization.name, "Ingrese el nombre de la organización.");
        requireText(errors, "organization.legal_id", organization.legal_id, "Ingrese la identificación.");
        requireEmail(errors, "organization.email", organization.email);
        requirePhone(errors, "organization.phone", organization.phone);
        requireText(errors, "organization.country", organization.country, "Seleccione el país.");
        requireText(errors, "organization.location_one", organization.location_one, "Seleccione la provincia o estado.");
        requireText(errors, "organization.location_two", organization.location_two, "Seleccione el cantón o ciudad.");
        if (state.org_type === "provider") {
            requireText(errors, "organization.description", organization.description, "Ingrese la descripción del proveedor.");
        }
        return errors;
    }
    if (step === "bank_accounts") {
        if (!state.bank_accounts.length) {
            errors.bank_accounts = "Agregue al menos una cuenta bancaria.";
            return errors;
        }
        const seenIbans = new Set();
        state.bank_accounts.forEach((account, index) => {
            const parsed = organizationBankAccountSchema.safeParse(account);
            if (!parsed.success) {
                parsed.error.issues.forEach((issue) => {
                    const path = issue.path.join(".");
                    errors[`bank_accounts.${index}.${path}`] = issue.message;
                });
                return;
            }
            if (seenIbans.has(parsed.data.iban)) {
                errors[`bank_accounts.${index}.iban`] = "El IBAN ya está agregado.";
                return;
            }
            seenIbans.add(parsed.data.iban);
        });
        return errors;
    }
    if (step === "billing") {
        if (state.org_type !== "concierge") {
            return errors;
        }
        const billing = state.billing_setup;
        requireText(errors, "billing_setup.plan_id", billing.plan_id, "Seleccione el plan.");
        requireText(errors, "billing_setup.plan_price_id", billing.plan_price_id, "Seleccione la versión de precio.");
        if (billing.trial.enabled && !billing.trial.days) {
            errors["billing_setup.trial.days"] = "Ingrese los días de prueba.";
        }
        if (billing.grace_days_after_due === null || billing.grace_days_after_due < 0) {
            errors["billing_setup.grace_days_after_due"] =
                "Ingrese los días de gracia.";
        }
        return errors;
    }
    requireText(errors, "owner.assigned_person", state.owner.assigned_person, "Ingrese el nombre del propietario.");
    requireEmail(errors, "owner.email", state.owner.email);
    if (!state.owner.password || state.owner.password.length < 8) {
        errors["owner.password"] =
            "La contraseña debe tener al menos 8 caracteres.";
    }
    return errors;
}
function requireText(errors, key, value, message) {
    if (!value?.trim()) {
        errors[key] = message;
    }
}
function requireEmail(errors, key, value) {
    const email = value?.trim() ?? "";
    if (!email) {
        errors[key] = "Ingrese el correo.";
        return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors[key] = "Ingrese un correo válido.";
    }
}
function requirePhone(errors, key, value) {
    const phone = value?.trim() ?? "";
    if (!phone) {
        errors[key] = "Ingrese el teléfono.";
        return;
    }
    if (!isPossiblePhoneNumberValue(phone)) {
        errors[key] = "Ingrese un teléfono internacional válido.";
    }
}
